import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface PolicyUpdateModalProps {
  open: boolean;
  onAccept: () => void;
  policyType: "privacy" | "terms";
  newVersion: string;
  effectiveDate: string;
}

export const PolicyUpdateModal = ({
  open,
  onAccept,
  policyType,
  newVersion,
  effectiveDate,
}: PolicyUpdateModalProps) => {
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleAccept = async () => {
    if (!accepted) {
      toast.error("Please accept the updated policy to continue");
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // Record the consent
        await supabase.functions.invoke("record-consent", {
          body: {
            consents: [
              {
                type: policyType,
                given: true,
                version: newVersion,
              },
            ],
          },
        });
      }

      onAccept();
      toast.success("Thank you for accepting the updated policy");
    } catch (error: any) {
      console.error("Error recording policy acceptance:", error);
      toast.error("Failed to record acceptance. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const policyTitle = policyType === "privacy" ? "Privacy Policy" : "Terms of Service";
  const policyLink = policyType === "privacy" ? "/privacy" : "/terms";

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl">Updated {policyTitle}</DialogTitle>
          <DialogDescription>
            We've updated our {policyTitle}. Please review and accept the changes to continue
            using Decor Canvas.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-muted p-4 rounded-lg">
            <p className="text-sm font-semibold mb-1">What's Changed?</p>
            <p className="text-sm text-muted-foreground">
              We've made improvements to our {policyTitle} to better protect your rights and
              provide more transparency about how we handle your data.
            </p>
          </div>

          <div className="space-y-2">
            <p className="text-sm">
              <strong>New Version:</strong> {newVersion}
            </p>
            <p className="text-sm">
              <strong>Effective Date:</strong>{" "}
              {new Date(effectiveDate).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </div>

          <div className="border rounded-lg p-4">
            <p className="text-sm font-semibold mb-2">Review Full Document</p>
            <Link to={policyLink} target="_blank">
              <Button variant="outline" size="sm" className="w-full">
                Open {policyTitle} in New Tab
              </Button>
            </Link>
          </div>

          <div className="flex items-start gap-2 pt-4">
            <Checkbox
              id="accept-policy"
              checked={accepted}
              onCheckedChange={(checked) => setAccepted(checked as boolean)}
            />
            <Label htmlFor="accept-policy" className="text-sm cursor-pointer font-normal">
              I have read and accept the updated {policyTitle} (Version {newVersion})
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={handleAccept} disabled={!accepted || loading} className="w-full">
            {loading ? "Processing..." : "Accept and Continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
